import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import { X, Check, UserPlus, CloudUpload } from 'lucide-react';
import TrDateInput from '../components/TrDateInput';

interface Gayrimenkul {
  gayrimenkulID: number;
  takipNo: string;
  kategori: number;
  baslik: string;
  adres: string;
  metrekare: number;
  kat: string | null;
  durum: number;
  projeID: number | null;
  aktifMi: boolean;
  proje?: { projeID: number; projeAdi: string } | null;
}

interface Cari {
  cariID: number;
  takipNo: string;
  adiSoyadi: string;
  firmaAdi?: string;
  telefon1: string;
  telefon2?: string;
  ePosta?: string;
  adres?: string;
  turu: string;
  aktifMi: boolean;
}

interface KiraSozlesmesi {
  kiraSozlesmeID: number;
  takipNo: string;
  gayrimenkulID: number;
  cariID: number;
  baslangicTarihi: string;
  bitisTarihi: string;
  kiraBedeli: number;
  odemeGunu: number;
  kapora: number;
  depositoBirimi: string;
  teminatMektubu: boolean;
  odemePeriyodu: string;
  yillikArtisOrani: number;
  vergiFaturaDurumu: string;
  kefilAdSoyad?: string | null;
  kefilTelefon?: string | null;
  ekKosullar?: string | null;
  aktifMi: boolean;
  gayrimenkul?: Gayrimenkul | null;
  cari?: Cari | null;
}

const KATEGORI_MAP: Record<number, string> = { 0: 'Daire', 1: 'Ofis', 2: 'Dükkân' };
const DURUM_MAP: Record<number, string> = { 0: 'Boş', 1: 'Kirada', 2: 'Bakımda' };

const DURUM_STR_TO_NUM: Record<string, number> = { 'Bos': 0, 'Kirada': 1, 'Bakimda': 2 };
const KATEGORI_STR_TO_NUM: Record<string, number> = { 'Daire': 0, 'Ofis': 1, 'Dukkan': 2 };

const normalizeGayrimenkul = (g: any): Gayrimenkul => ({
    ...g,
    durum: typeof g.durum === 'string' ? (DURUM_STR_TO_NUM[g.durum] ?? g.durum) : g.durum,
    kategori: typeof g.kategori === 'string' ? (KATEGORI_STR_TO_NUM[g.kategori] ?? g.kategori) : g.kategori,
});

const KiraSozlesmeler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sozlesmeler, setSozlesmeler] = useState<KiraSozlesmesi[]>([]);
  const [gayrimenkuller, setGayrimenkuller] = useState<Gayrimenkul[]>([]);
  const [cariler, setCariler] = useState<Cari[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDurum, setFilterDurum] = useState('aktif');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHizliMusteriModal, setShowHizliMusteriModal] = useState(false);
  const [hizliMusteriSaving, setHizliMusteriSaving] = useState(false);
  const [hizliMusteriForm, setHizliMusteriForm] = useState({
    adiSoyadi: '', firmaAdi: '', telefon1: '', telefon2: '', ePosta: '', adres: '', tc: '', cariVD: '', cariVN: '', turu2: '', aktifMi: true
  });
  const [hizliMusteriAltKategoriler, setHizliMusteriAltKategoriler] = useState<{cariAltKategoriID: number; altKategoriAdi: string}[]>([]);
  const [formData, setFormData] = useState({
    gayrimenkulID: '',
    cariID: '',
    baslangicTarihi: '',
    bitisTarihi: '',
    kiraBedeli: '',
    odemeGunu: '1',
    odemePeriyodu: 'Aylik',
    yillikArtisOrani: '',
    vergiFaturaDurumu: 'Muaf',
    deposito: '',
    depositoBirimi: '₺',
    teminatMektubu: false,
    kefilAdSoyad: '',
    kefilTelefon: '',
    ekKosullar: '',
  });
  const [artisTuru, setArtisTuru] = useState('TUFE');
  const [enflasyonVerisi, setEnflasyonVerisi] = useState<{ tufe: number | null; ufe: number | null; donem: string } | null>(null);
  const [enflasyonLoading, setEnflasyonLoading] = useState(false);
  const [showKefilForm, setShowKefilForm] = useState(false);

  useEffect(() => {
    fetchSozlesmeler();
    fetchGayrimenkuller();
    fetchCariler();
  }, []);

  useEffect(() => {
    if (artisTuru !== 'TUFE' && artisTuru !== 'UIFE') {
      setEnflasyonVerisi(null);
      return;
    }
    const now = new Date();
    const yil = now.getFullYear();
    const ay = now.getMonth() + 1;
    setEnflasyonLoading(true);
    axiosInstance.get(`/api/EnflasyonOranlari/kira-artis/${yil}/${ay}`)
      .then(res => {
        const { tufeOrani, ufeOrani } = res.data;
        const ayAdi = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'][res.data.ay - 1];
        setEnflasyonVerisi({ tufe: tufeOrani, ufe: ufeOrani, donem: `${ayAdi} ${res.data.yil}` });
        const oran = artisTuru === 'TUFE' ? tufeOrani : ufeOrani;
        if (oran != null) setFormData(fd => ({ ...fd, yillikArtisOrani: String(oran) }));
        else setFormData(fd => ({ ...fd, yillikArtisOrani: '' }));
      })
      .catch(() => {
        setEnflasyonVerisi(null);
        setFormData(fd => ({ ...fd, yillikArtisOrani: '' }));
        toast.warning('Enflasyon verisi bulunamadı. Ayarlar → Finansal Tanımlamalar bölümünden giriş yapabilirsiniz.');
      })
      .finally(() => setEnflasyonLoading(false));
  }, [artisTuru]);

  // Portföy sayfasından "Sözleşme Yap" ile gelindiyse modal'ı otomatik aç
  useEffect(() => {
    const preselectedID = (location.state as any)?.gayrimenkulID;
    if (preselectedID && gayrimenkuller.length > 0) {
      setFormData(prev => ({ ...prev, gayrimenkulID: String(preselectedID) }));
      setShowModal(true);
      // State'i temizle (geri gelindiğinde tekrar açılmasın)
      window.history.replaceState({}, '');
    }
  }, [gayrimenkuller, location.state]);

  const fetchSozlesmeler = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/KiraSozlesmeleri');
      setSozlesmeler(response.data);
    } catch (error) {
      toast.error('Sözleşmeler yüklenirken bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const fetchGayrimenkuller = async () => {
    try {
      const response = await axiosInstance.get('/api/KiralikGayrimenkuller?aktifMi=true');
      setGayrimenkuller(response.data.map(normalizeGayrimenkul));
    } catch (error) {
      console.error('Gayrimenkuller yüklenemedi:', error);
    }
  };

  const fetchCariler = async () => {
    try {
      const response = await axiosInstance.get('/api/Cariler?tur=M%C3%BC%C5%9Fteri');
      setCariler(response.data.filter((c: Cari) => c.aktifMi));
    } catch (error) {
      console.error('Cariler yüklenemedi:', error);
    }
  };

  const fetchHizliMusteriAltKategoriler = async () => {
    try {
      const res = await axiosInstance.get('/api/CariAltKategorileri/aktif?turu=M%C3%BC%C5%9Fteri');
      setHizliMusteriAltKategoriler(res.data);
    } catch { /* sessiz */ }
  };

  const handleOpenHizliMusteri = () => {
    setHizliMusteriForm({ adiSoyadi: '', firmaAdi: '', telefon1: '', telefon2: '', ePosta: '', adres: '', tc: '', cariVD: '', cariVN: '', turu2: '', aktifMi: true });
    fetchHizliMusteriAltKategoriler();
    setShowHizliMusteriModal(true);
  };

  const handleHizliMusteriSave = async () => {
    if (!hizliMusteriForm.adiSoyadi.trim()) { toast.warning('Ad Soyad zorunludur.'); return; }
    if (!hizliMusteriForm.telefon1.trim()) { toast.warning('Telefon zorunludur.'); return; }
    setHizliMusteriSaving(true);
    try {
      const payload = {
        adiSoyadi: hizliMusteriForm.adiSoyadi,
        firmaAdi: hizliMusteriForm.firmaAdi || null,
        telefon1: hizliMusteriForm.telefon1.replace(/\D/g, '').slice(0, 10),
        telefon2: hizliMusteriForm.telefon2 ? hizliMusteriForm.telefon2.replace(/\D/g, '').slice(0, 10) : null,
        ePosta: hizliMusteriForm.ePosta || null,
        adres: hizliMusteriForm.adres || null,
        tc: hizliMusteriForm.tc || null,
        cariVD: hizliMusteriForm.cariVD || null,
        cariVN: hizliMusteriForm.cariVN || null,
        turu: 'Müşteri',
        turu2: hizliMusteriForm.turu2 || null,
        aktifMi: hizliMusteriForm.aktifMi
      };
      const res = await axiosInstance.post('/api/Cariler', payload);
      toast.success('Müşteri başarıyla eklendi.');
      await fetchCariler();
      setFormData(prev => ({ ...prev, cariID: String(res.data.cariID) }));
      setShowHizliMusteriModal(false);
    } catch {
      toast.error('Müşteri eklenirken hata oluştu.');
    } finally {
      setHizliMusteriSaving(false);
    }
  };

  const bosGayrimenkuller = gayrimenkuller.filter(g => g.durum === 0);

  const selectedGayrimenkul = gayrimenkuller.find(g => g.gayrimenkulID === Number(formData.gayrimenkulID));
  const selectedCari = cariler.find(c => c.cariID === Number(formData.cariID));

  const handleCreate = async () => {
    if (!formData.gayrimenkulID || !formData.cariID || !formData.baslangicTarihi || !formData.bitisTarihi || !formData.kiraBedeli) {
      toast.warning('Lütfen tüm zorunlu alanları doldurun.');
      return;
    }
    try {
      setSaving(true);
      await axiosInstance.post('/api/KiraSozlesmeleri', {
        gayrimenkulID: Number(formData.gayrimenkulID),
        cariID: Number(formData.cariID),
        baslangicTarihi: formData.baslangicTarihi,
        bitisTarihi: formData.bitisTarihi,
        kiraBedeli: parseFloat(formData.kiraBedeli.replace(/\./g, '').replace(',', '.')) || 0,
        odemeGunu: Number(formData.odemeGunu),
        odemePeriyodu: formData.odemePeriyodu,
        yillikArtisOrani: parseFloat(formData.yillikArtisOrani) || 0,
        vergiFaturaDurumu: formData.vergiFaturaDurumu,
        kapora: parseFloat(formData.deposito.replace(/\./g, '').replace(',', '.')) || 0,
        depositoBirimi: formData.depositoBirimi === '₺' ? 'TRY' : formData.depositoBirimi === '$' ? 'USD' : 'EUR',
        teminatMektubu: formData.teminatMektubu,
        kefilAdSoyad: formData.kefilAdSoyad || null,
        kefilTelefon: formData.kefilTelefon || null,
        ekKosullar: formData.ekKosullar || null,
      });
      toast.success('Kira sözleşmesi başarıyla oluşturuldu!');
      setShowModal(false);
      resetForm();
      fetchSozlesmeler();
      fetchGayrimenkuller();
    } catch (error: any) {
      const msg = error?.response?.data || 'Sözleşme oluşturulurken hata oluştu!';
      toast.error(typeof msg === 'string' ? msg : 'Sözleşme oluşturulurken hata oluştu!');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({ gayrimenkulID: '', cariID: '', baslangicTarihi: '', bitisTarihi: '', kiraBedeli: '', odemeGunu: '1', odemePeriyodu: 'Aylik', yillikArtisOrani: '', vergiFaturaDurumu: 'Muaf', deposito: '', depositoBirimi: '₺', teminatMektubu: false, kefilAdSoyad: '', kefilTelefon: '', ekKosullar: '' });
    setArtisTuru('TUFE');
    setShowKefilForm(false);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('tr-TR');

  const isAktif = (s: KiraSozlesmesi) => s.aktifMi && new Date(s.bitisTarihi) >= new Date();
  const isSuresiDolmus = (s: KiraSozlesmesi) => s.aktifMi && new Date(s.bitisTarihi) < new Date();

  const filteredSozlesmeler = sozlesmeler.filter(s => {
    const matchesSearch = searchTerm === '' ||
      s.takipNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.cari?.adiSoyadi.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.gayrimenkul?.baslik.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesDurum = true;
    if (filterDurum === 'aktif') matchesDurum = isAktif(s);
    else if (filterDurum === 'suresi-dolmus') matchesDurum = isSuresiDolmus(s);
    else if (filterDurum === 'iptal') matchesDurum = !s.aktifMi;

    return matchesSearch && matchesDurum;
  });

  const toplamAktif = sozlesmeler.filter(s => isAktif(s)).length;
  const toplamSuresiDolmus = sozlesmeler.filter(s => isSuresiDolmus(s)).length;
  const toplamIptal = sozlesmeler.filter(s => !s.aktifMi).length;
  const toplamKira = sozlesmeler.filter(s => isAktif(s)).reduce((sum, s) => sum + s.kiraBedeli, 0);

  const getDurumInfo = (s: KiraSozlesmesi) => {
    if (!s.aktifMi) return { label: 'İptal', border: '#ef4444', bg: '#fef2f2', badgeBg: '#fee2e2', badgeColor: '#dc2626' };
    if (isSuresiDolmus(s)) return { label: 'Süresi Dolmuş', border: '#f59e0b', bg: '#fffbeb', badgeBg: '#fef9c3', badgeColor: '#ca8a04' };
    return { label: 'Aktif', border: '#16a34a', bg: '#f0fdf4', badgeBg: '#dcfce7', badgeColor: '#15803d' };
  };

  return (
    <>
      <style>{`
        .ks-wrap { background: #f8fafc; min-height: 100vh; padding: 24px; font-family: 'Inter', 'Segoe UI', sans-serif; }
        .ks-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 40px; }
        .ks-page-header-left { display: flex; flex-direction: column; gap: 4px; }
        .ks-page-title { font-size: 22px; font-weight: 700; color: #0f172a; letter-spacing: -0.4px; margin: 0; }
        .ks-page-subtitle { font-size: 13.5px; color: #64748b; margin: 0; }
        .ks-stat-row { display: flex; gap: 16px; margin-bottom: 24px; }
        .ks-stat-card { flex: 1; background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 4px rgba(15,23,42,0.05); padding: 18px 22px; display: flex; align-items: center; gap: 16px; }
        .ks-stat-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
        .ks-stat-num { font-size: 26px; font-weight: 700; color: #0f172a; line-height: 1; }
        .ks-stat-label { font-size: 11px; color: #94a3b8; margin-top: 4px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; }
        .ks-filter-card { background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 4px rgba(15,23,42,0.05); padding: 16px 20px; margin-bottom: 24px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .ks-search-wrap { position: relative; flex: 1; min-width: 280px; }
        .ks-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 13px; pointer-events: none; }
        .ks-search-input { width: 100%; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 9px 14px 9px 38px; font-size: 13.5px; color: #0f172a; outline: none; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit; box-sizing: border-box; }
        .ks-search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.08); background: #fff; }
        .ks-search-input::placeholder { color: #94a3b8; }
        .ks-select { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 36px 8px 12px; font-size: 13.5px; color: #0f172a; outline: none; cursor: pointer; transition: border-color 0.15s; font-family: inherit; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; min-width: 150px; }
        .ks-select:focus { border-color: #cd6906; box-shadow: 0 0 0 3px rgba(205,105,6,0.1); background-color: #fff; }
        .ks-btn-clear { background: none; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 9px 18px; font-size: 13px; color: #64748b; cursor: pointer; font-family: inherit; font-weight: 500; transition: all 0.15s; white-space: nowrap; }
        .ks-btn-clear:hover { border-color: #cbd5e1; color: #0f172a; background: #f8fafc; }
        .ks-list-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
        .ks-list-title { font-size: 16px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 10px; }
        .ks-count-chip { background: #f1f5f9; color: #64748b; border-radius: 20px; padding: 2px 10px; font-size: 12px; font-weight: 600; }
        .ks-btn-new { background: #cd6906; border: none; border-radius: 8px; padding: 10px 20px; font-size: 13px; color: #fff; cursor: pointer; font-family: inherit; font-weight: 600; transition: background 0.15s, box-shadow 0.15s; display: flex; align-items: center; gap: 7px; box-shadow: 0 4px 12px rgba(205,105,6,0.3); }
        .ks-btn-new:hover { background: #b85d05; }
        .ks-table-cols { display: flex; align-items: center; padding: 0 24px 10px; gap: 0; }
        .ks-th { font-size: 11px; font-weight: 600; color: #94a3b8; letter-spacing: 0.5px; }
        .ks-th.kiraci { flex: 0 0 180px; }
        .ks-th.divider-h { width: 33px; flex-shrink: 0; }
        .ks-th.gayrimenkul { flex: 0 0 180px; }
        .ks-th.durum { flex: 0 0 110px; }
        .ks-th.tarih { flex: 0 0 170px; }
        .ks-th.kira { flex: 1; }
        .ks-th.islem { flex: 0 0 90px; }
        .ks-panel { background: #fff; border-radius: 12px; border: 1px solid #e5e7eb; box-shadow: 0 1px 2px rgba(0,0,0,0.04); margin-bottom: 6px; display: flex; align-items: stretch; overflow: visible; transition: box-shadow 0.15s, transform 0.12s; position: relative; }
        .ks-panel:hover { background: #fef9f4; box-shadow: 0 3px 10px rgba(15,23,42,0.08); transform: translateY(-1px); cursor: pointer; }
        .ks-panel-accent { display: none; }
        .ks-panel-body { display: flex; align-items: center; padding: 12px 16px; flex: 1; gap: 0; }
        .ks-col-kiraci { flex: 0 0 180px; overflow: hidden; }
        .ks-kiraci-name { font-size: 13px; font-weight: 600; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ks-kiraci-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
        .ks-takipno { font-size: 11px; color: #d1d5db; margin-top: 2px; font-weight: 500; letter-spacing: 0.3px; }
        .ks-divider { width: 1px; background: #f3f4f6; align-self: stretch; margin: 0 16px; flex-shrink: 0; }
        .ks-col-gayrimenkul { flex: 0 0 180px; overflow: hidden; }
        .ks-gm-name { font-size: 13px; font-weight: 500; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ks-gm-sub { font-size: 11.5px; color: #6b7280; margin-top: 3px; display: flex; align-items: center; gap: 4px; }
        .ks-col-durum { flex: 0 0 110px; display: flex; align-items: center; }
        .ks-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 600; border-radius: 20px; padding: 3px 10px; }
        .ks-badge-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
        .ks-col-tarih { flex: 0 0 170px; }
        .ks-tarih-label { font-size: 10.5px; color: #9ca3af; font-weight: 500; letter-spacing: 0.3px; text-transform: uppercase; }
        .ks-tarih-val { font-size: 12.5px; font-weight: 500; color: #374151; margin-top: 3px; }
        .ks-col-kira { flex: 1; }
        .ks-kira-label { font-size: 10.5px; color: #9ca3af; font-weight: 500; letter-spacing: 0.3px; text-transform: uppercase; }
        .ks-kira-val { font-size: 13px; font-weight: 700; color: #111827; margin-top: 3px; letter-spacing: -0.2px; font-variant-numeric: tabular-nums; }
        .ks-col-islem { flex: 0 0 90px; display: flex; align-items: center; justify-content: flex-end; }
        .ks-btn-detay { background: transparent; border: 1px solid #d1d5db; border-radius: 6px; padding: 5px 11px; font-size: 12px; font-weight: 500; color: #374151; cursor: pointer; font-family: inherit; transition: all 0.15s; display: flex; align-items: center; gap: 6px; white-space: nowrap; }
        .ks-btn-detay:hover { background: #cd6906; border-color: #cd6906; color: #fff; }
        .ks-empty { background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; padding: 64px 24px; text-align: center; }
        .ks-empty-icon { font-size: 42px; margin-bottom: 14px; opacity: 0.22; }
        .ks-empty-text { font-size: 14px; color: #94a3b8; font-weight: 500; }
        .ks-loading { background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; padding: 64px 24px; text-align: center; }
        @keyframes ks-spin { to { transform: rotate(360deg); } }
        .ks-spinner { width: 32px; height: 32px; border: 3px solid #f1f5f9; border-top-color: #3b82f6; border-radius: 50%; animation: ks-spin 0.8s linear infinite; margin: 0 auto 14px; }
        .ks-loading-text { font-size: 14px; color: #94a3b8; font-weight: 500; }
        /* Modal */
        .ks-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15,23,42,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; }
        .ks-modal { background: #fff; border-radius: 16px; box-shadow: 0 25px 50px rgba(0,0,0,0.15); width: 95%; max-width: 700px; max-height: 90vh; overflow-y: auto; }
        .ks-modal-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; }
        .ks-modal-title { font-size: 17px; font-weight: 700; color: #0f172a; }
        .ks-modal-close { background: none; border: none; font-size: 20px; color: #94a3b8; cursor: pointer; padding: 4px; border-radius: 6px; transition: all 0.15s; }
        .ks-modal-close:hover { background: #f1f5f9; color: #0f172a; }
        .ks-modal-body { padding: 24px; }
        .ks-modal-footer { padding: 16px 24px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 10px; }
        .ks-form-group { margin-bottom: 18px; }
        .ks-form-label { display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px; letter-spacing: 0.3px; }
        .ks-form-input, .ks-form-select { width: 100%; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; font-size: 13.5px; color: #0f172a; outline: none; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit; box-sizing: border-box; }
        .ks-form-input:focus, .ks-form-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.08); background: #fff; }
        .ks-form-select { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; cursor: pointer; }
        .ks-form-row { display: flex; gap: 16px; }
        .ks-form-row .ks-form-group { flex: 1; }
        .ks-info-box { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 10px; padding: 12px 16px; margin-bottom: 18px; }
        .ks-info-box-title { font-size: 11px; font-weight: 600; color: #0369a1; letter-spacing: 0.3px; text-transform: uppercase; margin-bottom: 6px; }
        .ks-info-box-row { font-size: 13px; color: #334155; margin-bottom: 2px; }
        .ks-info-box-row strong { font-weight: 600; color: #0f172a; }
        .ks-btn-save { background: #cd6906; border: none; border-radius: 10px; padding: 10px 24px; font-size: 13px; color: #fff; cursor: pointer; font-family: inherit; font-weight: 600; transition: background 0.15s; }
        .ks-btn-save:hover { background: #b85d05; }
        .ks-btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
        .ks-btn-cancel { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 10px 24px; font-size: 13px; color: #64748b; cursor: pointer; font-family: inherit; font-weight: 500; transition: all 0.15s; }
        .ks-btn-cancel:hover { border-color: #cbd5e1; color: #0f172a; background: #f8fafc; }
        /* Responsive */
        @media (max-width: 1200px) {
          .ks-stat-row { flex-wrap: wrap; }
          .ks-stat-card { flex: 1 1 calc(50% - 8px); min-width: 0; }
          .ks-table-cols { display: none; }
          .ks-panel-body { flex-wrap: wrap; gap: 12px 16px; }
          .ks-col-kiraci, .ks-col-gayrimenkul, .ks-col-durum, .ks-col-tarih, .ks-col-kira, .ks-col-islem { flex: unset; width: auto; }
          .ks-col-kiraci { flex: 1 1 180px; }
          .ks-divider { display: none; }
          .ks-col-gayrimenkul { flex: 1 1 150px; }
        }
        @media (max-width: 768px) {
          .ks-wrap { padding: 16px; }
          .ks-stat-card { flex: 1 1 100%; }
          .ks-filter-card { flex-direction: column; align-items: stretch; }
          .ks-search-wrap { min-width: unset; }
          .ks-select { width: 100%; }
          .ks-list-header { flex-wrap: wrap; gap: 10px; }
          .ks-panel-body { flex-direction: column; gap: 10px; padding: 14px 16px; }
          .ks-col-kiraci, .ks-col-gayrimenkul, .ks-col-durum, .ks-col-tarih, .ks-col-kira, .ks-col-islem { flex: unset; width: 100%; }
          .ks-col-islem { justify-content: flex-start; }
          .ks-form-row { flex-direction: column; gap: 0; }
        }
      `}</style>

      <div className="ks-wrap">
        {/* Page Header */}
        <div className="ks-page-header">
          <div className="ks-page-header-left">
            <h1 className="ks-page-title">Kira Sözleşmeleri</h1>
            <p className="ks-page-subtitle">Aktif ve geçmiş kira sözleşmelerinizi takip edin.</p>
          </div>
          <button className="ks-btn-new" onClick={() => { resetForm(); setShowModal(true); }}>
            <span style={{ fontSize: 17, lineHeight: 1 }}>＋</span>
            Yeni Sözleşme
          </button>
        </div>

        {/* Stats Row */}
        <div className="ks-stat-row">
          <div className="ks-stat-card">
            <div className="ks-stat-icon" style={{ background: '#f0f9ff' }}>
              <i className="fas fa-file-contract" style={{ color: '#0ea5e9' }}></i>
            </div>
            <div>
              <div className="ks-stat-num">{sozlesmeler.filter(s => s.aktifMi).length}</div>
              <div className="ks-stat-label">Toplam Sözleşme</div>
            </div>
          </div>
          <div className="ks-stat-card">
            <div className="ks-stat-icon" style={{ background: '#f0fdf4' }}>
              <i className="fas fa-check-circle" style={{ color: '#16a34a' }}></i>
            </div>
            <div>
              <div className="ks-stat-num">{toplamAktif}</div>
              <div className="ks-stat-label">Aktif Sözleşme</div>
            </div>
          </div>
          <div className="ks-stat-card">
            <div className="ks-stat-icon" style={{ background: '#fffbeb' }}>
              <i className="fas fa-clock" style={{ color: '#f59e0b' }}></i>
            </div>
            <div>
              <div className="ks-stat-num">{toplamSuresiDolmus}</div>
              <div className="ks-stat-label">Süresi Dolmuş</div>
            </div>
          </div>
          <div className="ks-stat-card">
            <div className="ks-stat-icon" style={{ background: '#f5f3ff' }}>
              <i className="fas fa-coins" style={{ color: '#8b5cf6' }}></i>
            </div>
            <div>
              <div className="ks-stat-num" style={{ fontSize: 17 }}>
                {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(toplamKira)}
              </div>
              <div className="ks-stat-label">Aylık Toplam Kira</div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="ks-filter-card">
          <div className="ks-search-wrap">
            <i className="fas fa-search ks-search-icon"></i>
            <input
              type="text"
              className="ks-search-input"
              placeholder="Takip no, kiracı adı veya gayrimenkul ara..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select className="ks-select" value={filterDurum} onChange={e => setFilterDurum(e.target.value)}>
            <option value="">DURUM — Tümü</option>
            <option value="aktif">● Aktif</option>
            <option value="suresi-dolmus">● Süresi Dolmuş</option>
            <option value="iptal">● İptal Edilmiş</option>
          </select>
          <button className="ks-btn-clear" onClick={() => { setSearchTerm(''); setFilterDurum(''); }}>
            Temizle
          </button>
        </div>

        {/* List Header */}
        <div className="ks-list-header">
          <div className="ks-list-title">
            Sözleşme Listesi
            <span className="ks-count-chip">{filteredSozlesmeler.length}</span>
          </div>
        </div>

        {/* Column Headers */}
        {!loading && filteredSozlesmeler.length > 0 && (
          <div className="ks-table-cols">
            <span className="ks-th kiraci">Kiracı</span>
            <span className="ks-th divider-h" />
            <span className="ks-th gayrimenkul">Gayrimenkul</span>
            <span className="ks-th durum">Durum</span>
            <span className="ks-th tarih">Sözleşme Tarihi</span>
            <span className="ks-th kira">Kira Bedeli</span>
            <span className="ks-th islem">İşlemler</span>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="ks-loading">
            <div className="ks-spinner" />
            <div className="ks-loading-text">Sözleşmeler yükleniyor...</div>
          </div>
        ) : filteredSozlesmeler.length === 0 ? (
          <div className="ks-empty">
            <div className="ks-empty-icon">
              <i className="fas fa-file-contract" style={{ color: '#cbd5e1' }}></i>
            </div>
            <div className="ks-empty-text">
              {searchTerm || filterDurum
                ? 'Arama kriterlerinize uygun sözleşme bulunamadı.'
                : 'Henüz hiç kira sözleşmesi eklenmemiş.'}
            </div>
          </div>
        ) : (
          filteredSozlesmeler.map(sozlesme => {
            const cfg = getDurumInfo(sozlesme);
            return (
              <div
                key={sozlesme.kiraSozlesmeID}
                className="ks-panel"
                onClick={() => navigate(`/kiralik/sozlesme-detay/${sozlesme.takipNo}`)}
              >
                <div className="ks-panel-accent" style={{ background: cfg.border }} />
                <div className="ks-panel-body">
                  {/* Kiracı */}
                  <div className="ks-col-kiraci">
                    <div className="ks-kiraci-name">{sozlesme.cari?.adiSoyadi || '—'}</div>
                    {sozlesme.cari?.telefon1 && (
                      <div className="ks-kiraci-sub">{sozlesme.cari.telefon1}</div>
                    )}
                    <div className="ks-takipno">#{sozlesme.takipNo}</div>
                  </div>

                  <div className="ks-divider" />

                  {/* Gayrimenkul */}
                  <div className="ks-col-gayrimenkul">
                    <div className="ks-gm-name">{sozlesme.gayrimenkul?.baslik || '—'}</div>
                    <div className="ks-gm-sub">
                      <i className="fas fa-building" style={{ fontSize: 10, color: '#94a3b8' }}></i>
                      {KATEGORI_MAP[sozlesme.gayrimenkul?.kategori ?? 0]} • {sozlesme.gayrimenkul?.metrekare ?? 0} m²
                    </div>
                    {sozlesme.gayrimenkul?.proje && (
                      <div className="ks-gm-sub">
                        <i className="fas fa-project-diagram" style={{ fontSize: 10, color: '#94a3b8' }}></i>
                        {sozlesme.gayrimenkul.proje.projeAdi}
                      </div>
                    )}
                  </div>

                  {/* Durum */}
                  <div className="ks-col-durum">
                    <span className="ks-badge" style={{ background: cfg.badgeBg, color: cfg.badgeColor }}>
                      <span className="ks-badge-dot" style={{ background: cfg.border }} />
                      {cfg.label}
                    </span>
                  </div>

                  {/* Tarih */}
                  <div className="ks-col-tarih">
                    <div className="ks-tarih-label">Başlangıç / Bitiş</div>
                    <div className="ks-tarih-val">
                      {formatDate(sozlesme.baslangicTarihi)} — {formatDate(sozlesme.bitisTarihi)}
                    </div>
                  </div>

                  {/* Kira Bedeli */}
                  <div className="ks-col-kira">
                    <div className="ks-kira-label">Aylık Kira</div>
                    <div className="ks-kira-val">{formatCurrency(sozlesme.kiraBedeli)}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                      Her ayın {sozlesme.odemeGunu}. günü
                    </div>
                  </div>

                  {/* İşlemler */}
                  <div className="ks-col-islem" onClick={e => e.stopPropagation()}>
                    <button
                      className="ks-btn-detay"
                      onClick={() => navigate(`/kiralik/sozlesme-detay/${sozlesme.takipNo}`)}
                    >
                      <i className="fas fa-eye" style={{ fontSize: 12 }}></i>
                      Detay
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Yeni Sözleşme Modal */}
      {showModal && (() => {
        const inp: React.CSSProperties = {
          width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
          borderRadius: 8, fontSize: 13, color: '#111827', outline: 'none',
          background: '#fff', boxSizing: 'border-box',
        };
        const lbl: React.CSSProperties = {
          display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4,
        };
        return (
          <div
            onClick={() => setShowModal(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 1050,
              background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 768,
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0,
              }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>Yeni Kira Sözleşmesi</div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Kiracı ve gayrimenkul bilgilerini doldurun</div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
                    padding: 4, borderRadius: 8, display: 'flex', alignItems: 'center',
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* Row 1: Portföy + Kiracı */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Gayrimenkul */}
                  <div>
                    <label style={lbl}>Gayrimenkul *</label>
                    <select
                      value={formData.gayrimenkulID}
                      onChange={e => setFormData({ ...formData, gayrimenkulID: e.target.value })}
                      style={inp}
                    >
                      <option value="">— Boş gayrimenkul seçin —</option>
                      {bosGayrimenkuller.map(g => (
                        <option key={g.gayrimenkulID} value={g.gayrimenkulID}>
                          {g.baslik} ({KATEGORI_MAP[g.kategori]} • {g.metrekare} m²)
                        </option>
                      ))}
                    </select>
                    {selectedGayrimenkul && (
                      <div style={{
                        marginTop: 8, padding: '8px 12px', background: '#f9fafb',
                        border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, color: '#374151',
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{selectedGayrimenkul.baslik}</div>
                        <div style={{ color: '#6b7280' }}>{KATEGORI_MAP[selectedGayrimenkul.kategori]} • {selectedGayrimenkul.metrekare} m²</div>
                        {selectedGayrimenkul.adres && <div style={{ color: '#6b7280', marginTop: 2 }}>{selectedGayrimenkul.adres}</div>}
                      </div>
                    )}
                  </div>

                  {/* Kiracı */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <label style={{ ...lbl, marginBottom: 0 }}>Kiracı (Müşteri) *</label>
                      <button
                        type="button"
                        onClick={handleOpenHizliMusteri}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: 12, fontWeight: 500, color: '#cd6906',
                          display: 'flex', alignItems: 'center', gap: 4, padding: '2px 4px',
                          borderRadius: 6,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(205,105,6,0.08)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                      >
                        <UserPlus size={13} /> Hızlı Ekle
                      </button>
                    </div>
                    <select
                      value={formData.cariID}
                      onChange={e => setFormData({ ...formData, cariID: e.target.value })}
                      style={inp}
                    >
                      <option value="">— Müşteri seçin —</option>
                      {cariler.map(c => (
                        <option key={c.cariID} value={c.cariID}>
                          {c.adiSoyadi}{c.firmaAdi ? ` (${c.firmaAdi})` : ''} — {c.telefon1}
                        </option>
                      ))}
                    </select>
                    {selectedCari && (
                      <div style={{
                        marginTop: 8, padding: '8px 12px', background: '#f9fafb',
                        border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, color: '#374151',
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{selectedCari.adiSoyadi}</div>
                        {selectedCari.firmaAdi && <div style={{ color: '#6b7280' }}>{selectedCari.firmaAdi}</div>}
                        <div style={{ color: '#6b7280' }}>{selectedCari.telefon1}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2: Başlangıç + Bitiş */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={lbl}>Başlangıç Tarihi *</label>
                    <TrDateInput
                      value={formData.baslangicTarihi}
                      onChange={e => setFormData({ ...formData, baslangicTarihi: e.target.value })}
                      style={inp}
                    />
                  </div>
                  <div>
                    <label style={lbl}>Bitiş Tarihi *</label>
                    <TrDateInput
                      value={formData.bitisTarihi}
                      onChange={e => setFormData({ ...formData, bitisTarihi: e.target.value })}
                      style={inp}
                    />
                  </div>
                </div>

                {/* Row 3: Kira Bedeli + Depozito */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={lbl}>Aylık Kira Bedeli (₺) *</label>
                    <input
                      type="text"
                      placeholder="Örn: 15.000"
                      value={formData.kiraBedeli ? Number(formData.kiraBedeli.replace(/\./g, '').replace(',', '.')).toLocaleString('tr-TR') : ''}
                      onChange={e => {
                        const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                        if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                          setFormData({ ...formData, kiraBedeli: raw });
                        }
                      }}
                      style={inp}
                    />
                  </div>
                  <div>
                    <label style={lbl}>Depozito</label>
                    <div style={{ display: 'flex', gap: 0 }}>
                      <input
                        type="text"
                        placeholder="Örn: 30.000"
                        value={formData.deposito ? Number(formData.deposito.replace(/\./g, '').replace(',', '.')).toLocaleString('tr-TR') : ''}
                        onChange={e => {
                          const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                          if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                            setFormData({ ...formData, deposito: raw });
                          }
                        }}
                        style={{ ...inp, borderRadius: '8px 0 0 8px', borderRight: 'none', flex: 1 }}
                      />
                      <select
                        value={formData.depositoBirimi}
                        onChange={e => setFormData({ ...formData, depositoBirimi: e.target.value })}
                        style={{ ...inp, width: 60, borderRadius: '0 8px 8px 0', padding: '8px 6px', flexShrink: 0, cursor: 'pointer' }}
                      >
                        <option value="₺">₺</option>
                        <option value="$">$</option>
                        <option value="€">€</option>
                      </select>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.teminatMektubu}
                        onChange={e => setFormData({ ...formData, teminatMektubu: e.target.checked })}
                        style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#cd6906' }}
                      />
                      Teminat Mektubu ile
                    </label>
                  </div>
                </div>

                {/* Row 4: Ödeme Günü + Ödeme Periyodu */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={lbl}>Ödeme Günü (1-31) *</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.odemeGunu}
                      onChange={e => setFormData({ ...formData, odemeGunu: e.target.value })}
                      style={inp}
                    />
                  </div>
                  <div>
                    <label style={lbl}>Ödeme Periyodu</label>
                    <select
                      value={formData.odemePeriyodu}
                      onChange={e => setFormData({ ...formData, odemePeriyodu: e.target.value })}
                      style={{ ...inp, cursor: 'pointer' }}
                    >
                      <option value="Aylik">Aylık</option>
                      <option value="3Aylik">3 Aylık</option>
                      <option value="6Aylik">6 Aylık</option>
                      <option value="Yillik">Yıllık</option>
                    </select>
                  </div>
                </div>

                {/* Row 4b: Artış Türü + Artış Oranı */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={lbl}>Artış Türü</label>
                    <select
                      value={artisTuru}
                      onChange={e => {
                        setArtisTuru(e.target.value);
                        if (e.target.value !== 'Sabit') setFormData(fd => ({ ...fd, yillikArtisOrani: '' }));
                      }}
                      style={{ ...inp, cursor: 'pointer' }}
                    >
                      <option value="TUFE">TÜFE (12 Aylık Ortalama)</option>
                      <option value="Sabit">Sabit Oran (%)</option>
                      <option value="YOK">Artış Uygulanmayacak</option>
                      <option value="UIFE">Yİ-ÜFE</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>
                      {artisTuru === 'TUFE' ? 'TÜFE Oranı (%)' : artisTuru === 'UIFE' ? 'Yİ-ÜFE Oranı (%)' : 'Sabit Artış Oranı (%)'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      placeholder={artisTuru === 'YOK' ? '—' : artisTuru === 'Sabit' ? 'Örn: 25' : 'Yükleniyor...'}
                      disabled={artisTuru !== 'Sabit'}
                      value={formData.yillikArtisOrani}
                      onChange={e => setFormData({ ...formData, yillikArtisOrani: e.target.value })}
                      style={{ ...inp, background: artisTuru !== 'Sabit' ? '#f3f4f6' : '#fff', color: artisTuru !== 'Sabit' ? '#374151' : '#111827', cursor: artisTuru !== 'Sabit' ? 'not-allowed' : 'text', fontWeight: (artisTuru === 'TUFE' || artisTuru === 'UIFE') && formData.yillikArtisOrani ? 600 : 400 }}
                    />
                    {enflasyonLoading && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Veri yükleniyor...</div>
                    )}
                    {!enflasyonLoading && (artisTuru === 'TUFE' || artisTuru === 'UIFE') && (
                      enflasyonVerisi
                        ? <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>✓ Kaynak: Enflasyon Oranları modülü ({enflasyonVerisi.donem})</div>
                        : <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>⚠ Veri bulunamadı — Ayarlar → Finansal Tanımlamalar'dan giriş yapın</div>
                    )}
                  </div>
                </div>

                {/* Row 5: Vergi / Fatura Durumu */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={lbl}>Vergi / Fatura Durumu</label>
                    <select
                      value={formData.vergiFaturaDurumu}
                      onChange={e => setFormData({ ...formData, vergiFaturaDurumu: e.target.value })}
                      style={{ ...inp, cursor: 'pointer' }}
                    >
                      <option value="Muaf">Muaf (Mesken)</option>
                      <option value="KdvDahil">KDV Dahil</option>
                      <option value="KdvHaric">KDV Hariç</option>
                      <option value="StopajDahil">Stopaj Dahil</option>
                    </select>
                  </div>
                </div>

                {/* Kefil Bilgileri Accordion */}
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => setShowKefilForm(p => !p)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', background: showKefilForm ? '#fef3e8' : '#f9fafb',
                      border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                      color: showKefilForm ? '#cd6906' : '#6b7280', transition: 'background 0.15s',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 16, lineHeight: 1 }}>{showKefilForm ? '−' : '+'}</span>
                      Kefil Bilgisi Ekle
                    </span>
                    <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>{showKefilForm ? 'Kapat' : 'Opsiyonel'}</span>
                  </button>
                  {showKefilForm && (
                    <div style={{ padding: '14px 14px 16px', background: '#fff', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div>
                        <label style={lbl}>Kefil Ad Soyad</label>
                        <input
                          type="text"
                          placeholder="Örn: Ahmet Yılmaz"
                          value={formData.kefilAdSoyad}
                          onChange={e => setFormData({ ...formData, kefilAdSoyad: e.target.value })}
                          style={inp}
                        />
                      </div>
                      <div>
                        <label style={lbl}>Kefil Telefon</label>
                        <input
                          type="text"
                          placeholder="05XX XXX XX XX"
                          value={formData.kefilTelefon}
                          onChange={e => setFormData({ ...formData, kefilTelefon: e.target.value })}
                          style={inp}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Açıklama */}
                <div>
                  <label style={lbl}>Açıklama / Ek Koşullar</label>
                  <textarea
                    rows={3}
                    placeholder="Sözleşmeye ait notlar..."
                    value={formData.ekKosullar}
                    onChange={e => setFormData({ ...formData, ekKosullar: e.target.value })}
                    style={{ ...inp, resize: 'vertical' }}
                  />
                </div>

                {/* Dosya Yükleme */}
                <div>
                  <label style={lbl}>Sözleşme Belgesi</label>
                  <div style={{
                    border: '2px dashed #d1d5db', borderRadius: 10, padding: '20px 16px',
                    background: '#f9fafb', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 6, cursor: 'pointer',
                  }}>
                    <CloudUpload size={28} color="#9ca3af" />
                    <span style={{ fontSize: 13, color: '#6b7280' }}>Dosyayı sürükleyin veya tıklayın</span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>PDF, DOCX, JPG — maks. 10 MB</span>
                    <input type="file" accept=".pdf,.docx,.jpg,.jpeg,.png" style={{ display: 'none' }} />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 24px', borderTop: '1px solid #e5e7eb', background: '#fff',
                display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0, borderRadius: '0 0 16px 16px',
              }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb',
                    background: '#fff', fontSize: 13, fontWeight: 500, color: '#374151', cursor: 'pointer',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  İptal
                </button>
                <button
                  onClick={handleCreate}
                  disabled={saving}
                  style={{
                    padding: '8px 20px', borderRadius: 8, border: 'none',
                    background: saving ? '#e5e7eb' : '#cd6906',
                    color: saving ? '#9ca3af' : '#fff',
                    fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                  onMouseEnter={e => { if (!saving) e.currentTarget.style.background = '#a85505'; }}
                  onMouseLeave={e => { if (!saving) e.currentTarget.style.background = '#cd6906'; }}
                >
                  {saving ? 'Kaydediliyor...' : <><Check size={14} /> Sözleşme Oluştur</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Hızlı Müşteri Ekle Modal */}
      {showHizliMusteriModal && (() => {
        const inp: React.CSSProperties = {
          width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
          borderRadius: 8, fontSize: 13, color: '#111827', outline: 'none',
          background: '#fff', boxSizing: 'border-box',
        };
        const lbl: React.CSSProperties = {
          display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4,
        };
        return (
          <div
            onClick={() => setShowHizliMusteriModal(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 1100,
              background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0,
              }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserPlus size={18} color="#cd6906" /> Hızlı Müşteri Ekle
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Yeni kiracı kaydı oluşturun</div>
                </div>
                <button
                  onClick={() => setShowHizliMusteriModal(false)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
                    padding: 4, borderRadius: 8, display: 'flex', alignItems: 'center',
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={lbl}>Adı Soyadı *</label>
                    <input style={inp} type="text" maxLength={100} value={hizliMusteriForm.adiSoyadi}
                      onChange={e => setHizliMusteriForm(p => ({ ...p, adiSoyadi: e.target.value }))} />
                  </div>
                  <div>
                    <label style={lbl}>Firma Adı</label>
                    <input style={inp} type="text" maxLength={100} value={hizliMusteriForm.firmaAdi}
                      onChange={e => setHizliMusteriForm(p => ({ ...p, firmaAdi: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={lbl}>Telefon 1 *</label>
                    <input style={inp} type="text" maxLength={14} placeholder="5XXXXXXXXX" value={hizliMusteriForm.telefon1}
                      onChange={e => setHizliMusteriForm(p => ({ ...p, telefon1: e.target.value }))} />
                  </div>
                  <div>
                    <label style={lbl}>Telefon 2</label>
                    <input style={inp} type="text" maxLength={14} placeholder="5XXXXXXXXX" value={hizliMusteriForm.telefon2}
                      onChange={e => setHizliMusteriForm(p => ({ ...p, telefon2: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={lbl}>E-Posta</label>
                    <input style={inp} type="email" maxLength={100} value={hizliMusteriForm.ePosta}
                      onChange={e => setHizliMusteriForm(p => ({ ...p, ePosta: e.target.value }))} />
                  </div>
                  <div>
                    <label style={lbl}>TC</label>
                    <input style={inp} type="text" maxLength={11} value={hizliMusteriForm.tc}
                      onChange={e => setHizliMusteriForm(p => ({ ...p, tc: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={lbl}>Vergi Dairesi</label>
                    <input style={inp} type="text" maxLength={100} value={hizliMusteriForm.cariVD}
                      onChange={e => setHizliMusteriForm(p => ({ ...p, cariVD: e.target.value }))} />
                  </div>
                  <div>
                    <label style={lbl}>Vergi No</label>
                    <input style={inp} type="text" maxLength={50} value={hizliMusteriForm.cariVN}
                      onChange={e => setHizliMusteriForm(p => ({ ...p, cariVN: e.target.value }))} />
                  </div>
                </div>
                {hizliMusteriAltKategoriler.length > 0 && (
                  <div>
                    <label style={lbl}>Alt Kategori</label>
                    <select style={inp} value={hizliMusteriForm.turu2}
                      onChange={e => setHizliMusteriForm(p => ({ ...p, turu2: e.target.value }))}>
                      <option value="">Seçiniz...</option>
                      {hizliMusteriAltKategoriler.map(k => (
                        <option key={k.cariAltKategoriID} value={k.altKategoriAdi}>{k.altKategoriAdi}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label style={lbl}>Adres</label>
                  <textarea style={{ ...inp, resize: 'vertical' }} rows={2} maxLength={500} value={hizliMusteriForm.adres}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, adres: e.target.value }))} />
                </div>
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 24px', borderTop: '1px solid #e5e7eb', background: '#fff',
                display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0, borderRadius: '0 0 16px 16px',
              }}>
                <button
                  onClick={() => setShowHizliMusteriModal(false)}
                  style={{
                    padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb',
                    background: '#fff', fontSize: 13, fontWeight: 500, color: '#374151', cursor: 'pointer',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  İptal
                </button>
                <button
                  onClick={handleHizliMusteriSave}
                  disabled={hizliMusteriSaving}
                  style={{
                    padding: '8px 20px', borderRadius: 8, border: 'none',
                    background: hizliMusteriSaving ? '#e5e7eb' : '#cd6906',
                    color: hizliMusteriSaving ? '#9ca3af' : '#fff',
                    fontSize: 13, fontWeight: 600, cursor: hizliMusteriSaving ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                  onMouseEnter={e => { if (!hizliMusteriSaving) e.currentTarget.style.background = '#a85505'; }}
                  onMouseLeave={e => { if (!hizliMusteriSaving) e.currentTarget.style.background = '#cd6906'; }}
                >
                  {hizliMusteriSaving ? 'Kaydediliyor...' : <><Check size={14} /> Müşteri Ekle</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default KiraSozlesmeler;
